import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReactFlow, Background, Controls, useNodesState, useEdgesState, MarkerType, Handle, Position } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "@dagrejs/dagre";
import { toast } from "react-hot-toast";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import {
  HiOutlineChevronLeft,
  HiOutlineSearch,
  HiViewList,
  HiOutlineShare,
  HiOutlineExternalLink,
  HiOutlineArrowRight,
  HiOutlineQuestionMarkCircle,
  HiOutlineLightBulb,
  HiOutlineLightningBolt,
  HiOutlineTag,
  HiOutlineChatAlt2,
  HiOutlineUser,
  HiOutlineDocumentText,
  HiOutlineClock,
  HiOutlineBeaker,
  HiOutlineFlag,
  HiOutlineGlobeAlt,
  HiOutlineDuplicate,
  HiOutlineDeviceMobile,
  HiOutlineCollection,
} from "react-icons/hi";
import { normalizeText } from "../../utils";
import { buildDependencyGraph, anchorIdFromProps, codeAnchorIds } from "./buildDependencyGraph";

/* ───────────────────────── Visual primitives ───────────────────────── */

const KIND_STYLE = {
  question: { bg: "#dbeafe", border: "#3b82f6", text: "#1e40af", round: false },
  guideline: { bg: "#d1fae5", border: "#10b981", text: "#065f46", round: false },
  action: { bg: "#ecfeff", border: "#06b6d4", text: "#155e75", round: false },
  code: { bg: "#e0e7ff", border: "#6366f1", text: "#3730a3", round: true },
  answer: { bg: "#fef9c3", border: "#eab308", text: "#713f12", round: true },
  context: { bg: "#f1f5f9", border: "#94a3b8", text: "#334155", round: false },
};
const MUTUELLE_STYLE = { bg: "#f3e8ff", border: "#a855f7", text: "#6b21a8", round: true };

const styleForNode = (node) => (node.kind === "code" && node.variant === "mutuelle" ? MUTUELLE_STYLE : KIND_STYLE[node.kind] || KIND_STYLE.context);

const FACET_ICONS = {
  patient: HiOutlineUser,
  admission: HiOutlineDocumentText,
  temporal: HiOutlineClock,
  laboratory: HiOutlineBeaker,
  flag: HiOutlineFlag,
  status: HiOutlineTag,
  source: HiOutlineDeviceMobile,
  always: HiOutlineGlobeAlt,
  duplicate: HiOutlineDuplicate,
  type: HiOutlineCollection,
};
const KIND_ICONS = {
  question: HiOutlineQuestionMarkCircle,
  guideline: HiOutlineLightBulb,
  action: HiOutlineLightningBolt,
  code: HiOutlineTag,
  answer: HiOutlineChatAlt2,
};

const iconForNode = (node) => (node.kind === "context" ? FACET_ICONS[node.facet] || HiOutlineDocumentText : KIND_ICONS[node.kind] || HiOutlineTag);

const EFFECT_OP_META = {
  ADD: { symbol: "+", cls: "border-green-300 bg-green-100 text-green-700" },
  REMOVE: { symbol: "−", cls: "border-red-300 bg-red-100 text-red-700" },
  REPLACE_OUT: { symbol: "−", cls: "border-amber-300 bg-amber-100 text-amber-700" },
  REPLACE_IN: { symbol: "+", cls: "border-amber-300 bg-amber-100 text-amber-700" },
  KEEP_EXCEPT: { symbol: "✓", cls: "border-orange-300 bg-orange-100 text-orange-700" },
  REMOVE_TYPE: { symbol: "−", cls: "border-red-300 bg-red-100 text-red-700" },
  KEEP_TYPE: { symbol: "✓", cls: "border-orange-300 bg-orange-100 text-orange-700" },
  DUPLICATE: { symbol: "⎘", cls: "border-blue-300 bg-blue-100 text-blue-700" },
  ANSWER: { symbol: "↳", cls: "border-purple-300 bg-purple-100 text-purple-700" },
};

const TYPE_FILTERS = [
  { key: "question", label: "Questions" },
  { key: "answer", label: "Réponses" },
  { key: "guideline", label: "Recommandations" },
  { key: "action", label: "Actions" },
  { key: "code", label: "Codes" },
  { key: "context", label: "Conditions" },
];
const ALL_KINDS = TYPE_FILTERS.map((f) => f.key);

const KIND_LABEL = { question: "Question", guideline: "Recommandation", action: "Action", answer: "Réponse", context: "Condition" };
const kindLabel = (node) => {
  if (node.kind === "code") return node.variant === "mutuelle" ? "Code mutuelle" : "Code acte";
  if (node.kind === "context" && node.facet === "type") return "Type d'analyse";
  return KIND_LABEL[node.kind] || "Élément";
};

const makeNodeFilter = (enabledKinds, search) => {
  const q = search ? normalizeText(search) : "";
  return (node) => enabledKinds.has(node.kind) && (!q || normalizeText(node.label).includes(q));
};

/* ───────────────────────── Connexions inspector (default view) ───────────────────────── */

const OperatorPill = ({ op }) => (
  <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold ${op === "OR" ? "bg-violet-100 text-violet-700" : "bg-slate-200 text-slate-600"}`}>{op === "OR" ? "OU" : "ET"}</span>
);

const InlineOp = ({ op }) => <span className="px-0.5 text-[10px] font-semibold lowercase text-slate-400">{op === "OR" ? "ou" : "et"}</span>;

// Compact connector between triggers of a group — trailing "+" (ET) / "ou" (OU) instead of a full-width divider.
const GroupConnector = ({ op }) => (
  <span className="ml-0.5 inline-flex h-4 items-center justify-center rounded bg-slate-200 px-1.5 text-[11px] font-bold text-slate-600">{op === "OR" ? "ou" : "+"}</span>
);

const NodeChip = ({ node, effectOp, negate, onClick }) => {
  const style = styleForNode(node);
  const Icon = iconForNode(node);
  const op = effectOp ? EFFECT_OP_META[effectOp] : null;
  return (
    <button
      type="button"
      onClick={onClick}
      title={node.label}
      className={`group inline-flex max-w-full items-center gap-1.5 border px-2 py-1 text-left text-xs font-medium shadow-sm transition hover:shadow-md hover:brightness-95 ${style.round ? "rounded-full" : "rounded-md"}`}
      style={{ backgroundColor: style.bg, borderColor: style.border, color: style.text }}
    >
      {op && <span className={`inline-flex h-4 min-w-4 items-center justify-center rounded border px-1 text-[10px] font-bold ${op.cls}`}>{op.symbol}</span>}
      <Icon className="h-3.5 w-3.5 flex-shrink-0 opacity-70" />
      <span className="truncate">{node.label}</span>
      {negate && <span className="rounded bg-red-100 px-1 text-[9px] font-bold uppercase text-red-600">exclu</span>}
      {node.sublabel && <span className="truncate text-[10px] font-normal opacity-60">· {node.sublabel}</span>}
    </button>
  );
};

const Section = ({ title, hint, children }) => (
  <div className="mb-5">
    <div className="mb-2 flex items-baseline gap-2">
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</h4>
      {hint && <span className="text-[10px] text-gray-400">{hint}</span>}
    </div>
    {children}
  </div>
);

// Groups trigger edges by their group, then by their originating trigger, keeping each trigger's
// multiple values together (a CODE_SOME's codes belong to one condition, not separate rows).
const buildTriggerGroups = (edges, nodeOf, visible) => {
  const groupMap = new Map();
  for (const edge of edges) {
    const node = nodeOf(edge.source);
    if (!node || !visible(node)) continue;
    if (!groupMap.has(edge.groupKey)) {
      groupMap.set(edge.groupKey, { groupKey: edge.groupKey, groupIndex: edge.groupIndex, operator: edge.groupOperator, groupsOperator: edge.groupsOperator, triggerMap: new Map() });
    }
    const group = groupMap.get(edge.groupKey);
    if (!group.triggerMap.has(edge.triggerKey)) {
      group.triggerMap.set(edge.triggerKey, { triggerKey: edge.triggerKey, triggerIndex: edge.triggerIndex, inlineOperator: edge.inlineOperator, negate: edge.negate, nodes: [] });
    }
    group.triggerMap.get(edge.triggerKey).nodes.push(node);
  }
  return [...groupMap.values()]
    .map((group) => ({ ...group, triggers: [...group.triggerMap.values()].sort((a, b) => a.triggerIndex - b.triggerIndex) }))
    .sort((a, b) => a.groupIndex - b.groupIndex);
};

const TriggerRow = ({ trigger, onRecenter, trailingOp = null }) => (
  <div className="flex flex-wrap items-center gap-1">
    {trigger.negate && <span className="mr-0.5 rounded bg-red-100 px-1 py-0.5 text-[9px] font-bold uppercase text-red-600">sauf</span>}
    {trigger.nodes.map((node, index) => (
      <React.Fragment key={node.id}>
        {index > 0 && trigger.inlineOperator && <InlineOp op={trigger.inlineOperator} />}
        <NodeChip node={node} onClick={() => onRecenter(node.id)} />
      </React.Fragment>
    ))}
    {trailingOp && <GroupConnector op={trailingOp} />}
  </div>
);

const GroupBlock = ({ group, onRecenter }) => (
  <div className="flex flex-col gap-1.5 rounded-lg border border-gray-200 bg-gray-50/60 p-2">
    {group.triggers.map((trigger, index) => (
      <TriggerRow key={trigger.triggerKey} trigger={trigger} onRecenter={onRecenter} trailingOp={index < group.triggers.length - 1 ? group.operator : null} />
    ))}
  </div>
);

const TriggerGroups = ({ edges, nodeOf, visible, onRecenter }) => {
  const groups = useMemo(() => buildTriggerGroups(edges, nodeOf, visible), [edges, nodeOf, visible]);

  if (groups.length === 0) return null;

  if (groups.length === 1) {
    const group = groups[0];
    return group.triggers.length === 1 ? <TriggerRow trigger={group.triggers[0]} onRecenter={onRecenter} /> : <GroupBlock group={group} onRecenter={onRecenter} />;
  }

  const groupsOperator = groups[0].groupsOperator;
  return (
    <div className="flex flex-col gap-2">
      {groups.map((group, index) => (
        <React.Fragment key={group.groupKey}>
          {index > 0 && (
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-gray-200" />
              <OperatorPill op={groupsOperator} />
              <div className="h-px flex-1 bg-gray-200" />
            </div>
          )}
          <GroupBlock group={group} onRecenter={onRecenter} />
        </React.Fragment>
      ))}
    </div>
  );
};

const ChipRow = ({ edges, endpoint, nodeOf, visible, onRecenter }) => {
  const chips = edges.map((edge) => ({ edge, node: nodeOf(endpoint === "source" ? edge.source : edge.target) })).filter(({ node }) => node && visible(node));
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map(({ edge, node }) => (
        <NodeChip key={edge.id} node={node} effectOp={edge.effectOp} negate={edge.negate} onClick={() => onRecenter(node.id)} />
      ))}
    </div>
  );
};

const ConnectionsInspector = ({ graph, anchorId, enabledKinds, search, onRecenter, onOpen }) => {
  const node = graph.nodes.get(anchorId);
  const nodeOf = (id) => graph.nodes.get(id);
  const visible = useMemo(() => makeNodeFilter(enabledKinds, search), [enabledKinds, search]);

  const { incoming, outgoing } = useMemo(() => {
    const inEdges = graph.inEdges.get(anchorId) || [];
    const outEdges = graph.outEdges.get(anchorId) || [];
    const by = (edges, kind) => edges.filter((e) => e.kind === kind);
    return {
      incoming: { trigger: by(inEdges, "trigger"), effect: by(inEdges, "effect"), owns: by(inEdges, "owns") },
      outgoing: { trigger: by(outEdges, "trigger"), effect: by(outEdges, "effect"), owns: by(outEdges, "owns") },
    };
  }, [graph, anchorId]);

  if (!node) return <div className="p-6 text-sm text-gray-500">Élément introuvable.</div>;

  const HeaderIcon = iconForNode(node);
  const headerStyle = styleForNode(node);

  const countVisible = (edges, endpoint) =>
    edges.reduce((n, edge) => {
      const target = nodeOf(endpoint === "source" ? edge.source : edge.target);
      return target && visible(target) ? n + 1 : n;
    }, 0);

  const sections = [
    { key: "in-trigger", title: "Déclenché par", count: countVisible(incoming.trigger, "source"), render: () => <TriggerGroups edges={incoming.trigger} nodeOf={nodeOf} visible={visible} onRecenter={onRecenter} /> },
    { key: "in-owns", title: "Réponse à la question", count: countVisible(incoming.owns, "source"), render: () => <ChipRow edges={incoming.owns} endpoint="source" nodeOf={nodeOf} visible={visible} onRecenter={onRecenter} /> },
    { key: "in-effect", title: "Modifié par", count: countVisible(incoming.effect, "source"), render: () => <ChipRow edges={incoming.effect} endpoint="source" nodeOf={nodeOf} visible={visible} onRecenter={onRecenter} /> },
    { key: "out-owns", title: "Réponses possibles", count: countVisible(outgoing.owns, "target"), render: () => <ChipRow edges={outgoing.owns} endpoint="target" nodeOf={nodeOf} visible={visible} onRecenter={onRecenter} /> },
    { key: "out-effect", title: "Effets", count: countVisible(outgoing.effect, "target"), render: () => <ChipRow edges={outgoing.effect} endpoint="target" nodeOf={nodeOf} visible={visible} onRecenter={onRecenter} /> },
    { key: "out-trigger", title: "Utilisé par", hint: "déclenche", count: countVisible(outgoing.trigger, "target"), render: () => <ChipRow edges={outgoing.trigger} endpoint="target" nodeOf={nodeOf} visible={visible} onRecenter={onRecenter} /> },
  ];

  const visibleSections = sections.filter((s) => s.count > 0);
  const isEmpty = visibleSections.length === 0;

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="mb-5 flex items-start gap-3 rounded-xl border-2 p-3" style={{ backgroundColor: headerStyle.bg, borderColor: headerStyle.border }}>
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/60">
          <HeaderIcon className="h-5 w-5" style={{ color: headerStyle.text }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide opacity-70" style={{ color: headerStyle.text }}>
            {kindLabel(node)}
          </div>
          <div className="break-words text-sm font-semibold leading-tight" style={{ color: headerStyle.text }}>
            {node.label}
          </div>
          {node.sublabel && (
            <div className="mt-0.5 truncate text-xs opacity-70" style={{ color: headerStyle.text }}>
              {node.sublabel}
            </div>
          )}
        </div>
        {node.navigable && node.editType && (
          <button
            type="button"
            onClick={() => onOpen(node)}
            className="flex flex-shrink-0 items-center gap-1 rounded-md bg-white/70 px-2 py-1 text-xs font-medium shadow-sm transition hover:bg-white"
            style={{ color: headerStyle.text }}
          >
            <HiOutlineExternalLink className="h-3.5 w-3.5" />
            Ouvrir
          </button>
        )}
      </div>

      {isEmpty ? (
        <div className="rounded-lg border border-dashed border-gray-200 py-10 text-center text-sm text-gray-400">
          {search || enabledKinds.size < TYPE_FILTERS.length ? "Aucune connexion ne correspond aux filtres." : "Aucune connexion pour le moment."}
        </div>
      ) : (
        visibleSections.map((section) => (
          <Section key={section.key} title={section.title} hint={section.hint}>
            {section.render()}
          </Section>
        ))
      )}

      {!isEmpty && (
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-gray-400">
          <HiOutlineArrowRight className="h-3 w-3" />
          Cliquez sur un élément pour le recentrer et explorer ses connexions.
        </p>
      )}
    </div>
  );
};

/* ───────────────────────── Graph view (secondary, depth-limited ego DAG) ───────────────────────── */

const handleStyle = { width: 6, height: 6, background: "#cbd5e1", border: "1px solid white" };
const NODE_W = 184;
const NODE_H = 46;
const OP_W = 46;
const OP_H = 28;
const ANCHOR_NEIGHBOR_CAP = 40;

const DepNode = ({ data }) => {
  const { node, isAnchor } = data;
  const style = styleForNode(node);
  const Icon = iconForNode(node);
  return (
    <>
      <Handle type="target" position={Position.Left} style={handleStyle} />
      <div
        className={`flex items-center gap-1.5 border-2 px-2.5 py-2 text-xs font-medium ${style.round ? "rounded-full" : "rounded-lg"} ${node.navigable ? "cursor-pointer" : "cursor-default"}`}
        style={{ backgroundColor: style.bg, borderColor: style.border, color: style.text, maxWidth: NODE_W, boxShadow: isAnchor ? `0 0 0 3px ${style.border}66` : "0 1px 2px rgba(0,0,0,0.06)" }}
        title={node.label}
      >
        <Icon className="h-3.5 w-3.5 flex-shrink-0 opacity-70" />
        <span className="truncate">{node.label}</span>
      </div>
      <Handle type="source" position={Position.Right} style={handleStyle} />
    </>
  );
};

const OperatorNode = ({ data }) => (
  <>
    <Handle type="target" position={Position.Left} style={handleStyle} />
    <div className={`rounded-md border px-2 py-0.5 text-[11px] font-bold ${data.operator === "OR" ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-300 bg-slate-100 text-slate-600"}`}>{data.operator === "OR" ? "OU" : "ET"}</div>
    <Handle type="source" position={Position.Right} style={handleStyle} />
  </>
);

const MoreNode = ({ data }) => (
  <>
    <Handle type="target" position={Position.Left} style={handleStyle} />
    <div className="rounded-md border border-dashed border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-500">{data.label}</div>
    <Handle type="source" position={Position.Right} style={handleStyle} />
  </>
);

const nodeTypes = { dep: DepNode, operator: OperatorNode, more: MoreNode };

const layout = (nodes, edges) => {
  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 28, ranksep: 110, marginx: 16, marginy: 16 });
  for (const node of nodes) {
    const isOp = node.type === "operator";
    g.setNode(node.id, { width: isOp ? OP_W : NODE_W, height: isOp ? OP_H : NODE_H });
  }
  for (const edge of edges) g.setEdge(edge.source, edge.target);
  dagre.layout(g);
  return nodes.map((node) => {
    const pos = g.node(node.id);
    return { ...node, sourcePosition: "right", targetPosition: "left", position: { x: pos.x - pos.width / 2, y: pos.y - pos.height / 2 } };
  });
};

const triggerEdgeStyle = (negate) => ({
  type: "bezier",
  style: { stroke: negate ? "#ef4444" : "#64748b", strokeWidth: 1.75, strokeDasharray: negate ? "5 3" : undefined },
  labelStyle: { fontSize: 10, fontWeight: 700, fill: "#ef4444" },
  labelBgStyle: { fill: "#fff", fillOpacity: 0.9 },
  markerEnd: { type: MarkerType.ArrowClosed, color: negate ? "#ef4444" : "#64748b", width: 16, height: 16 },
});

// Depth-capped BFS over both directions from the anchor, honoring the type/search filters.
const collectEgo = (graph, anchorId, depth, visible) => {
  const dist = new Map([[anchorId, 0]]);
  const queue = [anchorId];
  while (queue.length > 0) {
    const current = queue.shift();
    const d = dist.get(current);
    if (d >= depth) continue;
    const neighbors = [...(graph.inEdges.get(current) || []).map((e) => e.source), ...(graph.outEdges.get(current) || []).map((e) => e.target)];
    for (const next of neighbors) {
      const node = graph.nodes.get(next);
      if (!node || (next !== anchorId && !visible(node))) continue;
      if (!dist.has(next)) {
        dist.set(next, d + 1);
        queue.push(next);
      }
    }
  }
  return dist;
};

// Keeps the graph readable when the anchor itself is a hub: cap how many nodes hang directly off it.
const capAnchorNeighbors = (flowNodes, flowEdges, anchorId) => {
  const directNeighbors = [];
  for (const edge of flowEdges) {
    if (edge.source === anchorId) directNeighbors.push(edge.target);
    if (edge.target === anchorId) directNeighbors.push(edge.source);
  }
  const unique = [...new Set(directNeighbors)];
  if (unique.length <= ANCHOR_NEIGHBOR_CAP) return { nodes: flowNodes, edges: flowEdges };

  const dropped = new Set(unique.slice(ANCHOR_NEIGHBOR_CAP));
  const survivingEdges = flowEdges.filter((e) => !dropped.has(e.source) && !dropped.has(e.target));
  const connected = new Set([anchorId]);
  for (const e of survivingEdges) {
    connected.add(e.source);
    connected.add(e.target);
  }
  const nodes = flowNodes.filter((n) => connected.has(n.id));

  const moreId = "more:anchor";
  nodes.push({ id: moreId, type: "more", draggable: false, data: { label: `+${dropped.size} autres — affinez les filtres` }, position: { x: 0, y: 0 } });
  survivingEdges.push({ id: `${anchorId}->${moreId}`, source: anchorId, target: moreId, type: "bezier", style: { stroke: "#cbd5e1", strokeWidth: 1.25, strokeDasharray: "4 4" } });
  return { nodes, edges: survivingEdges };
};

const buildFlow = (graph, anchorId, depth, visible) => {
  const included = collectEgo(graph, anchorId, depth, visible);
  const has = (id) => included.has(id);

  const flowNodes = [];
  const flowEdges = [];
  const addedFlowNodes = new Set();
  const addFlowNode = (id, type, data, draggable = true) => {
    if (addedFlowNodes.has(id)) return;
    addedFlowNodes.add(id);
    flowNodes.push({ id, type, draggable, data, position: { x: 0, y: 0 } });
  };

  for (const id of included.keys()) addFlowNode(id, "dep", { node: graph.nodes.get(id), isAnchor: id === anchorId });

  let opCounter = 0;

  // Trigger edges → nested operator badges: a trigger's own values combine via its inline operator
  // (CODE_SOME → OU, CODE_EVERY → ET), then the group's triggers combine via the group operator.
  for (const id of included.keys()) {
    const triggerEdges = (graph.inEdges.get(id) || []).filter((e) => e.kind === "trigger" && has(e.source));
    const groups = new Map();
    for (const edge of triggerEdges) {
      if (!groups.has(edge.groupKey)) groups.set(edge.groupKey, { operator: edge.groupOperator, triggers: new Map() });
      const group = groups.get(edge.groupKey);
      if (!group.triggers.has(edge.triggerKey)) group.triggers.set(edge.triggerKey, { inlineOperator: edge.inlineOperator, negate: edge.negate, edges: [] });
      group.triggers.get(edge.triggerKey).edges.push(edge);
    }

    for (const [groupKey, group] of groups) {
      const triggerOutputs = [];
      for (const trigger of group.triggers.values()) {
        if (trigger.edges.length >= 2) {
          const opId = `op:${opCounter++}`;
          addFlowNode(opId, "operator", { operator: trigger.inlineOperator || "AND" }, false);
          for (const edge of trigger.edges) flowEdges.push({ id: edge.id, source: edge.source, target: opId, label: trigger.negate ? "sauf" : undefined, ...triggerEdgeStyle(trigger.negate) });
          triggerOutputs.push({ source: opId, negate: false });
        } else {
          triggerOutputs.push({ source: trigger.edges[0].source, negate: trigger.negate });
        }
      }

      if (triggerOutputs.length >= 2) {
        const gopId = `op:${opCounter++}`;
        addFlowNode(gopId, "operator", { operator: group.operator }, false);
        flowEdges.push({ id: `${gopId}->${id}`, source: gopId, target: id, ...triggerEdgeStyle(false) });
        triggerOutputs.forEach((out, index) => flowEdges.push({ id: `${out.source}->${gopId}:${index}`, source: out.source, target: gopId, label: out.negate ? "sauf" : undefined, ...triggerEdgeStyle(out.negate) }));
      } else {
        const out = triggerOutputs[0];
        flowEdges.push({ id: `${out.source}->${id}:${groupKey}`, source: out.source, target: id, label: out.negate ? "sauf" : undefined, ...triggerEdgeStyle(out.negate) });
      }
    }
  }

  // Effect + owns edges.
  for (const id of included.keys()) {
    for (const edge of (graph.outEdges.get(id) || []).filter((e) => has(e.target))) {
      if (edge.kind === "effect") {
        const op = EFFECT_OP_META[edge.effectOp];
        flowEdges.push({
          id: edge.id,
          source: id,
          target: edge.target,
          label: op?.symbol,
          labelStyle: { fontSize: 12, fontWeight: 700, fill: ["REMOVE", "REPLACE_OUT", "REMOVE_TYPE"].includes(edge.effectOp) ? "#dc2626" : edge.effectOp === "ADD" || edge.effectOp === "REPLACE_IN" ? "#16a34a" : "#475569" },
          labelBgStyle: { fill: "#fff", fillOpacity: 0.9 },
          type: "bezier",
          style: { stroke: "#94a3b8", strokeWidth: 1.75 },
          markerEnd: { type: MarkerType.ArrowClosed, color: "#94a3b8", width: 16, height: 16 },
        });
      } else if (edge.kind === "owns") {
        flowEdges.push({ id: edge.id, source: id, target: edge.target, type: "bezier", style: { stroke: "#cbd5e1", strokeWidth: 1.25, strokeDasharray: "4 4" }, markerEnd: { type: MarkerType.ArrowClosed, color: "#cbd5e1", width: 14, height: 14 } });
      }
    }
  }

  return capAnchorNeighbors(flowNodes, flowEdges, anchorId);
};

const DependencyGraphView = ({ graph, anchorId, depth, enabledKinds, search, onRecenter }) => {
  const visible = useMemo(() => makeNodeFilter(enabledKinds, search), [enabledKinds, search]);

  const { nodes: laidOut, edges: builtEdges } = useMemo(() => {
    if (!anchorId || !graph.nodes.has(anchorId)) return { nodes: [], edges: [] };
    const { nodes, edges } = buildFlow(graph, anchorId, depth === Infinity ? 99 : depth, visible);
    return { nodes: layout(nodes, edges), edges };
  }, [graph, anchorId, depth, visible]);

  const [nodes, setNodes, onNodesChange] = useNodesState(laidOut);
  const [edges, setEdges, onEdgesChange] = useEdgesState(builtEdges);

  useEffect(() => {
    setNodes(laidOut);
    setEdges(builtEdges);
  }, [laidOut, builtEdges, setNodes, setEdges]);

  const onNodeClick = (_event, flowNode) => {
    if (flowNode.type === "dep" && flowNode.data.node.id !== anchorId) onRecenter(flowNode.data.node.id);
  };

  if (!anchorId || nodes.length === 0) {
    return <div className="flex h-full items-center justify-center text-sm text-gray-400">Aucune connexion à afficher.</div>;
  }

  return (
    <ReactFlow key={`${anchorId}:${depth}`} nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onNodeClick={onNodeClick} nodeTypes={nodeTypes} fitView fitViewOptions={{ padding: 0.2 }} minZoom={0.1} maxZoom={2}>
      <Background color="#e2e8f0" gap={20} />
      <Controls showInteractive={false} />
    </ReactFlow>
  );
};

/* ───────────────────────── Shell (anchor navigation + filters + view toggle) ───────────────────────── */

const DEPTH_OPTIONS = [
  { value: 1, label: "Direct" },
  { value: 2, label: "2 niveaux" },
  { value: Infinity, label: "Tout" },
];

const SegmentedButton = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick} className={`px-2.5 py-1 text-xs font-medium transition ${active ? "bg-primary-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
    {children}
  </button>
);

const DependencyExplorer = ({ data, currentQuestionId = null, currentGuidelineId = null, currentActionId = null, currentCodes = null, className = "" }) => {
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [view, setView] = useState("inspector");
  const [depth, setDepth] = useState(1);
  const [search, setSearch] = useState("");
  const [enabledKinds, setEnabledKinds] = useState(() => new Set(ALL_KINDS));

  const graph = useMemo(() => buildDependencyGraph(data || {}), [data]);

  const tableCodeIds = useMemo(() => (currentCodes?.length > 1 ? codeAnchorIds(currentCodes) : []), [currentCodes]);
  const initialAnchorId = useMemo(
    () => anchorIdFromProps({ currentQuestionId, currentGuidelineId, currentActionId, currentCodes }) || tableCodeIds.find((id) => graph.nodes.has(id)) || null,
    [currentQuestionId, currentGuidelineId, currentActionId, currentCodes, tableCodeIds, graph],
  );

  const [history, setHistory] = useState(() => (initialAnchorId ? [initialAnchorId] : []));
  useEffect(() => {
    setHistory(initialAnchorId ? [initialAnchorId] : []);
  }, [initialAnchorId]);

  const anchorId = history[history.length - 1] || null;

  const onRecenter = useCallback((id) => {
    setHistory((prev) => (prev[prev.length - 1] === id ? prev : [...prev, id]));
  }, []);
  const onBack = useCallback(() => setHistory((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev)), []);
  const onJumpTo = useCallback((index) => setHistory((prev) => prev.slice(0, index + 1)), []);
  const onSetRoot = useCallback((id) => setHistory([id]), []);

  const onOpen = useCallback((node) => {
    if (node.editType && node.raw?._id) window.open(`/${node.editType}/${node.raw._id}/edit`, "_blank");
  }, []);

  const toggleKind = useCallback((key) => {
    setEnabledKinds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      if (!document.fullscreenElement) await containerRef.current.requestFullscreen();
      else await document.exitFullscreen();
    } catch {
      toast.error("Erreur lors du passage en plein écran");
    }
  }, []);

  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const breadcrumb = useMemo(() => history.filter((id) => graph.nodes.has(id)).map((id) => ({ id, label: graph.nodes.get(id).label })), [history, graph]);

  const anchorValid = anchorId && graph.nodes.has(anchorId);
  if (!data || (!anchorValid && !tableCodeIds.some((id) => graph.nodes.has(id)))) {
    return (
      <div className={`flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-8 ${className}`}>
        <div className="text-sm text-gray-500">Aucune dépendance à afficher</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white ${className} ${isFullscreen ? "!fixed !inset-0 !z-50 !rounded-none" : ""}`}
      style={{ height: isFullscreen ? "100vh" : "calc(100vh - 240px)" }}
    >
      <div className="flex-shrink-0 border-b border-gray-100 p-2">
        <div className="mb-2 flex items-center gap-2">
          <button type="button" onClick={onBack} disabled={history.length <= 1} className="rounded p-1 text-gray-500 enabled:hover:bg-gray-100 disabled:opacity-30" title="Retour">
            <HiOutlineChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto text-xs text-gray-500">
            {breadcrumb.map((crumb, index) => (
              <React.Fragment key={`${crumb.id}-${index}`}>
                {index > 0 && <span className="text-gray-300">›</span>}
                <button
                  type="button"
                  onClick={() => onJumpTo(index)}
                  className={`max-w-[140px] truncate rounded px-1 py-0.5 hover:bg-gray-100 ${index === breadcrumb.length - 1 ? "font-semibold text-gray-800" : ""}`}
                  title={crumb.label}
                >
                  {crumb.label}
                </button>
              </React.Fragment>
            ))}
          </div>
          <div className="flex flex-shrink-0 overflow-hidden rounded-md border border-gray-200">
            <SegmentedButton active={view === "inspector"} onClick={() => setView("inspector")}>
              <span className="flex items-center gap-1">
                <HiViewList className="h-3.5 w-3.5" /> Connexions
              </span>
            </SegmentedButton>
            <SegmentedButton active={view === "graph"} onClick={() => setView("graph")}>
              <span className="flex items-center gap-1 border-l border-gray-200 pl-2">
                <HiOutlineShare className="h-3.5 w-3.5" /> Graphe
              </span>
            </SegmentedButton>
          </div>
          <button type="button" onClick={toggleFullscreen} className="flex-shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100" title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}>
            {isFullscreen ? <MdFullscreenExit className="h-4 w-4" /> : <MdFullscreen className="h-4 w-4" />}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1" style={{ minWidth: 160 }}>
            <HiOutlineSearch className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filtrer par nom…"
              className="w-full rounded-md border border-gray-200 py-1 pl-7 pr-2 text-xs outline-none focus:border-primary-400"
            />
          </div>
          {view === "graph" && (
            <div className="flex flex-shrink-0 overflow-hidden rounded-md border border-gray-200">
              {DEPTH_OPTIONS.map((opt) => (
                <SegmentedButton key={String(opt.value)} active={depth === opt.value} onClick={() => setDepth(opt.value)}>
                  {opt.label}
                </SegmentedButton>
              ))}
            </div>
          )}
        </div>

        <div className="mt-2 flex flex-wrap gap-1.5">
          {TYPE_FILTERS.map((filter) => {
            const active = enabledKinds.has(filter.key);
            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => toggleKind(filter.key)}
                className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition ${active ? "border-gray-300 bg-white text-gray-700" : "border-gray-200 bg-gray-50 text-gray-300"}`}
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: active ? KIND_STYLE[filter.key].border : "#d1d5db" }} />
                {filter.label}
              </button>
            );
          })}
        </div>

        {tableCodeIds.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-2">
            <span className="text-[11px] text-gray-400">Codes du tableau :</span>
            {tableCodeIds.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => onSetRoot(id)}
                className={`rounded-full border px-2 py-0.5 font-mono text-[11px] transition ${id === breadcrumb[0]?.id ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                {graph.nodes.get(id)?.label || id}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1">
        {!anchorValid ? (
          <div className="flex h-full items-center justify-center p-8 text-sm text-gray-400">Aucune dépendance pour ce code.</div>
        ) : view === "inspector" ? (
          <ConnectionsInspector graph={graph} anchorId={anchorId} enabledKinds={enabledKinds} search={search} onRecenter={onRecenter} onOpen={onOpen} />
        ) : (
          <DependencyGraphView graph={graph} anchorId={anchorId} depth={depth} enabledKinds={enabledKinds} search={search} onRecenter={onRecenter} />
        )}
      </div>
    </div>
  );
};

export default DependencyExplorer;
